import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  TemplateRef,
  ViewChild,
  computed,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrnDialog, BrnDialogContent } from '@spartan-ng/brain/dialog';
import type { ClassValue } from 'clsx';
import { mergeClasses } from 'src/app/@core/utils/merge-classes';

/**
 * Arena bottom-sheet component (`app-bottom-sheet`).
 *
 * A reusable wrapper around `brn-dialog` that renders content as a bottom-sheet
 * overlay anchored to the bottom of the viewport. Intended for mobile contexts
 * where a popover or dialog needs a sheet-style presentation.
 *
 * Unlike `app-dialog` (which uses `@ContentChild` for templates), this component
 * accepts template refs as `@Input()` signals so that a parent component can
 * pass its own already-resolved `TemplateRef` instances programmatically.
 *
 * ### Inputs
 * - `state` — `'open' | 'closed'` (controlled, mirrors `brn-dialog`)
 * - `headerTemplate` — optional `TemplateRef` rendered above the body
 * - `bodyTemplate` — main scrollable content (required for anything to show)
 * - `footerTemplate` — optional `TemplateRef` pinned at the bottom
 * - `overlayClickable` — whether tapping the backdrop closes the sheet (default: `true`)
 * - `maxHeight` — CSS max-height of the sheet panel (default: `'85dvh'`)
 * - `showScrollbar` — whether the body is scrollable (default: `true`)
 * - `class` — extra Tailwind classes merged onto the sheet panel
 *
 * ### Outputs
 * - `stateChanged` — emits `'open' | 'closed'` whenever the sheet state changes
 *
 * ### Usage
 * ```html
 * <app-bottom-sheet
 *   [state]="sheetState()"
 *   [bodyTemplate]="myBodyRef"
 *   [footerTemplate]="myFooterRef"
 *   (stateChanged)="onStateChanged($event)"
 * />
 * ```
 */
@Component({
  selector: 'app-bottom-sheet',
  standalone: true,
  imports: [CommonModule, BrnDialog, BrnDialogContent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <brn-dialog
      #brnDialog
      [state]="state()"
      [closeOnOutsidePointerEvents]="overlayClickable()"
      [closeOnBackdropClick]="overlayClickable()"
      (stateChanged)="onStateChanged($event)"
    >
      <ng-template brnDialogContent>
        <!-- Backdrop -->
        <div
          class="fixed inset-0 z-40 bg-black/50"
          (click)="overlayClickable() && onBackdropClick()"
        ></div>

        <!-- Sheet panel -->
        <div class="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-2xl bg-white shadow-xl"
          [class]="panelClass()"
        >
          <!-- Drag handle -->
          <div class="mx-auto mt-3 mb-1 h-1.5 w-10 shrink-0 rounded-full bg-gray-300"></div>

          @if (headerTemplate()) {
            <div class="border-b border-gray-100 px-4 pb-3 pt-2">
              <ng-container *ngTemplateOutlet="headerTemplate()!" />
            </div>
          }

          <div
            class="flex-1 px-4 py-3"
            [style.overflowY]="showScrollbar() ? 'auto' : 'hidden'"
          >
            @if (bodyTemplate()) {
              <ng-container *ngTemplateOutlet="bodyTemplate()!" />
            }
          </div>

          @if (footerTemplate()) {
            <div class="border-t border-gray-100 px-4 pt-3 pb-8">
              <ng-container *ngTemplateOutlet="footerTemplate()!" />
            </div>
          }
        </div>
      </ng-template>
    </brn-dialog>
  `,
})
export class BottomSheetComponent implements OnInit, OnDestroy {
  @ViewChild('brnDialog', { read: BrnDialog }) private _brnDialog!: BrnDialog;

  // ── Template ref inputs (passed programmatically, not via @ContentChild) ──
  readonly headerTemplate = input<TemplateRef<any> | undefined>(undefined);
  readonly bodyTemplate = input<TemplateRef<any> | undefined>(undefined);
  readonly footerTemplate = input<TemplateRef<any> | undefined>(undefined);

  // ── Behaviour inputs ──────────────────────────────────────────────────────
  readonly state = input<'open' | 'closed'>('closed');
  readonly overlayClickable = input<boolean>(true);
  readonly maxHeight = input<string>('85dvh');
  readonly showScrollbar = input<boolean>(true);
  readonly userClass = input<ClassValue>('', { alias: 'class' });

  // ── Output ────────────────────────────────────────────────────────────────
  readonly stateChanged = output<'open' | 'closed'>();

  readonly panelClass = computed(() =>
    mergeClasses(`max-h-[${this.maxHeight()}]`, this.userClass()),
  );

  ngOnInit(): void {}
  ngOnDestroy(): void {}

  onStateChanged(state: 'open' | 'closed'): void {
    this.stateChanged.emit(state);
  }

  onBackdropClick(): void {
    this.stateChanged.emit('closed');
  }
}
